/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { GET_COSTUME_TYPE } from "../graphql/getCostumeFeatures.query";
import { GET_BOXES } from "../graphql/getBoxes.query";
import { GetTermAndConditions } from "../graphql/getTermsAndConditions";

const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetCostumeTypeMethods extends IPluginMethodMap {
  getCostumeType(args: any): Promise<any>;
}

export class GetCostumeTypeService implements IAgentPlugin {
  readonly methods: IGetCostumeTypeMethods;
  private exchangeRate: any = 1;
  private boxValuesArray: any = [];

  constructor() {
    this.methods = {
      getCostumeType: this.getCostumeType.bind(this),
    };
  }

  async getCostumeType(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_COSTUME_TYPE,
          variables: {
            filter: {
              _id: args?.childId,
            },
          },
          fetchPolicy: "no-cache",
        });

        const boxValues = await client.query({
          query: GET_BOXES,
          variables: {
            filter: {
              _id: {
                eq: args?.boxId,
              },
            },
          },
          fetchPolicy: "no-cache",
        });

        let boxSection = null;
        let boxPresentation = null;

        if (
          boxValues?.data?.getBoxes?.content &&
          is.not.empty(boxValues?.data?.getBoxes?.content)
        ) {
          const boxResponse = boxValues?.data?.getBoxes?.content[0];

          // Capture section and presentation directly from the box (most reliable source)
          boxSection = boxResponse?.section ?? null;
          boxPresentation = boxResponse?.presentation ?? null;

          if (
            boxResponse?.items &&
            is.existy(boxResponse?.items) &&
            is.not.empty(boxResponse?.items)
          ) {
            for (let obj of boxResponse?.items) {
              if (
                obj?.costumeValue &&
                is.existy(obj?.costumeValue) &&
                is.not.empty(obj?.costumeValue)
              ) {
                this.boxValuesArray.push(obj?.costumeValue?.id);
              }
            }
          }
        }

        this.exchangeRate = res?.data?.getCurrencyExchangeRate?.conversion_rate;
        if (res?.data?.getCostumeType) {
          const payload = this.formatData(res?.data?.getCostumeType, boxSection, boxPresentation);
          this.boxValuesArray = [];

          let termsConditions = [];
          try {
            const termsRes = await client.query({
              query: GetTermAndConditions,
              variables: {},
              fetchPolicy: "network-only",
            });
            termsConditions = termsRes?.data?.getTermsConditions?.content ?? [];
          } catch (termsErr) {
            console.error("Error fetching termsConditions:", termsErr);
          }

          return {
            ...payload,
            termsConditions,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatCostumeFeaturesList(data: any) {
    const emptyObj = { name: "...", id: "", isVisible: true };
    if (data && is.existy(data) && is.not.empty(data)) {
      for (let featureObj of data) {
        if (
          is.existy(featureObj) &&
          is.existy(featureObj?.costumeAttributes) &&
          is.not.empty(featureObj?.costumeAttributes)
        ) {
          let visibleFeatureCount: number = 0;
          for (let attributeObj of featureObj?.costumeAttributes) {
            if (is.existy(attributeObj) && attributeObj?.isVisible) {
              visibleFeatureCount++;
            }

            if (
              is.existy(attributeObj) &&
              is.existy(attributeObj?.costumeValues) &&
              is.not.empty(attributeObj?.costumeValues)
            ) {
              let visibleCostumeCount: number = 0;
              for (let costumeObj of attributeObj?.costumeValues) {
                if (
                  is.existy(costumeObj) &&
                  is.existy(costumeObj) &&
                  is.not.empty(costumeObj) &&
                  costumeObj?.isVisible
                ) {
                  if (this.boxValuesArray.includes(costumeObj?.id)) {
                    costumeObj.isCostumeValueSold = false;
                  }

                  visibleCostumeCount++;
                }
              }
              if (
                visibleCostumeCount > 0 &&
                !attributeObj?.costumeValues.some((e: any) => e.name === "...")
              ) {
                attributeObj?.costumeValues.unshift(emptyObj);
              }
            }
          }
          if (
            visibleFeatureCount > 0 &&
            !featureObj?.costumeAttributes.some((e: any) => e.name === "...")
          ) {
            featureObj?.costumeAttributes.unshift(emptyObj);
          }
        }
      }
    }

    return data;
  }

  private formatData(data: any, boxSection?: any, boxPresentation?: any) {
    return {
      headers: {
        // Prefer box-level section/presentation (always correct for this specific order).
        // Fall back to costumeType-level if box doesn't have it.
        presentation: boxPresentation ?? data?.presentation,
        section: boxSection ?? data?.section,
        costumeTypeName: data?.name,
        costumeTypeID: data?.id,
        status: "Ready",
      },
      costumeFeaturesList: this.formatCostumeFeaturesList(
        data?.costumeFeatures
      ),
      exchangeRate: this.exchangeRate,
    };
  }
}
